import { foldersApi } from '@frontend/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useFolders(parentId: string | null = null) {
  const queryClient = useQueryClient();

  const foldersQuery = useQuery({
    queryKey: ['folders', parentId],
    queryFn: () => foldersApi.list({ parentId: parentId ?? undefined }),
  });

  const createFolderMutation = useMutation({
    mutationFn: foldersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      visibility?: 'public' | 'private';
      sharedWith?: string[];
    }) => foldersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (id: string) => foldersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: ({
      folderId,
      documentId,
    }: {
      folderId: string;
      documentId: string;
    }) => foldersApi.removeDocument(folderId, documentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['folderDocuments', variables.folderId],
      });
    },
  });

  return {
    folders: foldersQuery.data?.folders ?? [],
    isLoading: foldersQuery.isLoading,
    isError: foldersQuery.isError,
    error: foldersQuery.error,
    createFolder: (
      name: string,
      visibility: 'public' | 'private' = 'private',
    ) =>
      createFolderMutation.mutateAsync({
        name,
        visibility,
        parentId: parentId ?? undefined,
      }),
    isCreating: createFolderMutation.isPending,
    updateFolder: updateFolderMutation.mutate,
    isUpdating: updateFolderMutation.isPending,
    deleteFolder: (id: string) => deleteFolderMutation.mutate(id),
    isDeleting: deleteFolderMutation.isPending,
    deleteDocument: deleteDocumentMutation.mutate,
    isDeletingDocument: deleteDocumentMutation.isPending,
  };
}

export function useFolderDocuments(folderId: string | undefined) {
  return useQuery({
    queryKey: ['folderDocuments', folderId],
    queryFn: () => foldersApi.getDocuments(folderId!),
    enabled: !!folderId,
  });
}
